"use client";

import { useState } from "react";
import {
  type AxisBounds,
  AxisRangeFields,
  ChartStyleFields,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  FitTable,
  ScatterChart,
  type ScatterFit,
} from "@/components/tool-kit/charts/scatter-chart";
import {
  Checkbox,
  Notice,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  colorIndex,
  MAX_SERIES,
  paletteColor,
} from "@/lib/tool-kit/charts/color";
import type { NumberFormat } from "@/lib/tool-kit/charts/format";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import {
  groupIndex,
  parseXYRows,
  type XYPoint,
  type XYTable,
} from "@/lib/tool-kit/charts/parse";
import {
  formatEquation,
  formatR2,
  type LinearFit,
  linearFit,
} from "@/lib/tool-kit/charts/regression";
import {
  type AxisRange,
  boundedRange,
  extent,
} from "@/lib/tool-kit/charts/scale";
import { CHART_INK } from "@/lib/tool-kit/palette";

/** English strings. A translated content file overrides these through `ui`. */
export const SCATTER_PLOT_MAKER_STRINGS = {
  dataLabel: "Your data — one point per line: x, y",
  dataHint:
    "Add a third value to put the point in a group, which gets its own color and marker. Separate with commas, tabs or semicolons — use semicolons if your numbers have decimal commas. A first line without numbers is read as a header.",
  exampleData:
    "Hours studied, Exam score\n1, 52\n2, 58\n2.5, 61\n3, 60\n4, 68\n4.5, 72\n5, 70\n6, 79\n7, 83\n8, 85",
  exampleTitle: "Exam Score by Hours Studied",
  exampleXTitle: "Hours studied",
  exampleYTitle: "Exam score",
  pointsDetected: "one:{count} point|other:{count} points",
  groupsDetected: "one:{count} group|other:{count} groups",
  trendLabel: "Trend line (least squares)",
  trendNone: "None",
  trendAll: "One line through all points",
  trendGroups: "One line per group",
  showEquation: "Show the equation and r² on the chart",
  rangesLabel: "Axis ranges — leave blank for automatic",
  xMin: "X from",
  xMax: "X to",
  yMin: "Y from",
  yMax: "Y to",
  rangeAuto: "Auto",
  badRange:
    "An axis range needs its start below its end, so that axis is automatic.",
  hiddenPoints:
    "one:{count} point is outside the axis ranges and is not shown.|other:{count} points are outside the axis ranges and are not shown.",
  otherGroup: "Other groups",
  noGroup: "No group",
  foldedGroups:
    "There are more than {max} groups, so {count} of them share one color as “Other groups”.",
  fitHeading: "Least-squares line",
  groupColumn: "Group",
  nColumn: "n",
  slopeColumn: "Slope (b)",
  interceptColumn: "Intercept (a)",
  rColumn: "r",
  r2Column: "r²",
  allPoints: "All points",
  noFit: "—",
  fitNote:
    "The line is ŷ = a + bx, fitted by ordinary least squares. r² is the share of the variance in y the line accounts for; it says nothing about cause.",
  labelsAndStyle: "Labels and style",
  chartTitle: "Chart title",
  xAxisTitle: "X-axis title",
  yAxisTitle: "Y-axis title",
  axisTitlePlaceholderX: "X",
  axisTitlePlaceholderY: "Y",
  colorLabel: "Point color (the first group)",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  valueLabels: "Label each point with (x, y)",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and the gridlines out of the plot, and sets the figure number in bold and the title in italics above the chart — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy data",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the data as a table",
  xColumn: "x",
  yColumn: "y",
  chartLabel: "Scatter plot",
  chartDescription:
    "Scatter plot of {points}. x runs from {xMin} to {xMax} and y from {yMin} to {yMax}.",
  fitDescription: " Least-squares line: {equation}, r² = {r2}.",
  enterData: "Enter at least two x, y pairs to draw the plot.",
  skippedLines:
    "one:Line {lines} is not an x, y pair and was left out.|other:Lines {lines} are not x, y pairs and were left out.",
} as const;

type Strings = typeof SCATTER_PLOT_MAKER_STRINGS;
type Format = NumberFormat;
type TrendMode = "none" | "all" | "groups";
type Fit = ScatterFit;

/** Least-squares fits for the chosen trend mode, with the colour each is drawn in. */
function fitsFor(
  mode: TrendMode,
  points: XYPoint[],
  group: number[],
  names: string[],
  strings: Strings,
  colorOf: (index: number) => string
): Fit[] {
  if (mode === "none") {
    return [];
  }
  if (mode === "all" || names.length < 2) {
    return [
      {
        name: strings.allPoints,
        color: names.length > 1 ? CHART_INK.text : colorOf(0),
        fit: linearFit(
          points.map((p) => p.x),
          points.map((p) => p.y)
        ),
      },
    ];
  }
  return names.map((name, index) => {
    const members = points.filter((_, i) => group[i] === index);
    return {
      name,
      color: colorOf(index),
      fit: linearFit(
        members.map((p) => p.x),
        members.map((p) => p.y)
      ),
    };
  });
}

interface Ranges {
  x: AxisRange;
  y: AxisRange;
}

/** What the reader should know about how their data was read. */
function scatterNotices(
  table: XYTable,
  folded: number,
  ranges: Ranges,
  strings: Strings,
  locale: string,
  fmt: Format
): string[] {
  const notes: string[] = [];
  const { points } = table;
  if (points.length < 2) {
    notes.push(strings.enterData);
  }
  if (table.skipped.length > 0) {
    notes.push(
      tPlural(strings.skippedLines, locale, table.skipped.length, {
        lines: table.skipped.map((n) => fmt(n)).join(", "),
      })
    );
  }
  if (folded > 0) {
    notes.push(
      t(strings.foldedGroups, { max: fmt(MAX_SERIES), count: fmt(folded) })
    );
  }
  if (ranges.x.bad || ranges.y.bad) {
    notes.push(strings.badRange);
  }
  const hidden = points.filter(
    (p) =>
      p.x < ranges.x.min ||
      p.x > ranges.x.max ||
      p.y < ranges.y.min ||
      p.y > ranges.y.max
  ).length;
  if (hidden > 0) {
    notes.push(
      tPlural(strings.hiddenPoints, locale, hidden, { count: fmt(hidden) })
    );
  }
  return notes;
}

/** The `<desc>` a screen reader announces: the data's extent and the fitted line. */
function describeScatter(
  points: XYPoint[],
  overall: LinearFit | null,
  strings: Strings,
  locale: string,
  fmt: Format
): string {
  const number = (value: number) => fmt(value, { maximumFractionDigits: 4 });
  const [xLow, xHigh] = extent(points.map((p) => p.x)) ?? [0, 0];
  const [yLow, yHigh] = extent(points.map((p) => p.y)) ?? [0, 0];
  const base = t(strings.chartDescription, {
    points: tPlural(strings.pointsDetected, locale, points.length, {
      count: fmt(points.length),
    }),
    xMin: number(xLow),
    xMax: number(xHigh),
    yMin: number(yLow),
    yMax: number(yHigh),
  });
  if (!overall) {
    return base;
  }
  return (
    base +
    t(strings.fitDescription, {
      equation: formatEquation(overall, fmt),
      r2: formatR2(overall, fmt),
    })
  );
}

export default function ScatterPlotMakerWidget() {
  const strings = useToolUi(SCATTER_PLOT_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleData);
  const [trend, setTrend] = useState<TrendMode>("all");
  const [showEquation, setShowEquation] = useState(true);
  const [bounds, setBounds] = useState<AxisBounds>({
    xMin: "",
    xMax: "",
    yMin: "",
    yMax: "",
  });
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: strings.exampleXTitle,
    yTitle: strings.exampleYTitle,
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const table = parseXYRows(raw);
  const { points } = table;
  const groups = groupIndex(
    points.map((p) => p.group),
    {
      max: MAX_SERIES,
      otherLabel: strings.otherGroup,
      ungroupedLabel: strings.noGroup,
    }
  );
  const grouped = groups.names.length > 1;
  const start = colorIndex(style.color);
  const fits = fitsFor(
    trend,
    points,
    groups.index,
    groups.names,
    strings,
    (i) => paletteColor(start, i)
  );
  const ranges: Ranges = {
    x: boundedRange(
      points.map((p) => p.x),
      parseNumberInput(bounds.xMin),
      parseNumberInput(bounds.xMax)
    ),
    y: boundedRange(
      points.map((p) => p.y),
      parseNumberInput(bounds.yMin),
      parseNumberInput(bounds.yMax)
    ),
  };
  const notices = scatterNotices(
    table,
    groups.folded,
    ranges,
    strings,
    locale,
    fmt
  );
  const header = [
    table.header?.[0]?.trim() || strings.xColumn,
    table.header?.[1]?.trim() || strings.yColumn,
  ];
  const tableHead = grouped ? [...header, strings.groupColumn] : header;
  const cells = (p: XYPoint) =>
    grouped ? [p.x, p.y, p.group ?? strings.noGroup] : [p.x, p.y];
  const number = (value: number | string) =>
    typeof value === "number"
      ? fmt(value, { maximumFractionDigits: 4 })
      : value;
  const drawable = points.length >= 2;
  const counts = [
    tPlural(strings.pointsDetected, locale, points.length, {
      count: fmt(points.length),
    }),
  ];
  if (grouped) {
    counts.push(
      tPlural(strings.groupsDetected, locale, groups.names.length, {
        count: fmt(groups.names.length),
      })
    );
  }
  const trendOptions: { value: TrendMode; label: string }[] = [
    { value: "none", label: strings.trendNone },
    { value: "all", label: strings.trendAll },
  ];
  if (grouped) {
    trendOptions.push({ value: "groups", label: strings.trendGroups });
  }

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.dataHint}
        id="spm-data"
        label={strings.dataLabel}
        onChange={setRaw}
        rows={8}
        value={raw}
      />
      <div aria-live="polite" className="space-y-2">
        {drawable ? <p className={HINT}>{counts.join(" · ")}</p> : null}
        {notices.map((notice) => (
          <Notice key={notice}>{notice}</Notice>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="spm-trend"
          label={strings.trendLabel}
          onChange={setTrend}
          options={trendOptions}
          value={trend === "groups" && !grouped ? "all" : trend}
        />
        <div className="sm:pt-7">
          <Checkbox
            checked={showEquation}
            label={strings.showEquation}
            onChange={setShowEquation}
          />
        </div>
      </div>
      {drawable ? (
        <ChartFigure
          apa={style.apa}
          data={toTsv([tableHead, ...points.map(cells)])}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title, "scatter-plot")}
          labels={strings}
          note={style.note}
          table={{
            head: tableHead,
            rows: points.map((p) => cells(p).map(number)),
          }}
          title={style.title}
        >
          <ScatterChart
            description={describeScatter(
              points,
              fits.length === 1 ? fits[0].fit : null,
              strings,
              locale,
              fmt
            )}
            fits={fits}
            fmt={fmt}
            id="spm-chart"
            label={style.title.trim() || strings.chartLabel}
            names={groups.names}
            points={points.map((p, i) => ({
              x: p.x,
              y: p.y,
              group: groups.index[i],
              key: p.line,
            }))}
            showEquation={showEquation}
            style={style}
            xRange={ranges.x}
            yRange={ranges.y}
          />
        </ChartFigure>
      ) : null}
      {drawable && fits.length > 0 ? (
        <FitTable fits={fits} fmt={fmt} labels={strings} />
      ) : null}
      <ChartStyleFields
        axisLabels={strings}
        colorLabels={strings}
        idPrefix="spm"
        labels={strings}
        style={style}
        update={update}
        valueLabel={strings.valueLabels}
      >
        <AxisRangeFields
          bounds={bounds}
          idPrefix="spm"
          labels={strings}
          onChange={setBounds}
        />
      </ChartStyleFields>
    </div>
  );
}
